/**
 * scene-asset-lifecycle.ts — P05 分場景 preload + atlas unload helper.
 *
 * Tracks which Phaser texture/atlas keys each scene "owns" and releases them
 * when the scene shuts down so the GPU/CPU memory footprint doesn't pile up
 * across scene switches (especially: 學習端 ↔ 戰鬥端 toggles on iPad / 低階機).
 *
 * Design notes (see docs/GAME_TECHNIQUES_WIKI.md P05):
 *   - Per-scene registry kept in a WeakMap so registrations die with the scene
 *     instance — no manual book-keeping required.
 *   - `unloadSceneAssets` is **idempotent**: calling it twice on the same key
 *     is a no-op (we check `textures.exists` before removing).
 *   - `PROTECTED_KEYS` lists assets used by multiple scenes (BootScene splash,
 *     共用 font, etc.) that must NEVER be unloaded — callers can extend it.
 *   - `measureMemoryMB()` reads `performance.memory.usedJSHeapSize` when the
 *     browser exposes it (Chromium-family only) so 量測 before/after lives in
 *     one place. Returns null on Firefox/Safari/node-vitest (no DOM perf API).
 *   - `dispatchModeSwitch` fires a `ef:mode-switch` CustomEvent so mode-switch
 *     handlers (學習端 ↔ 戰鬥端) can unload the *opposite* mode's BGM / sprite
 *     without each call site having to know about every other module.
 *
 * Red-line constraints (CLAUDE.md / task spec):
 *   - Pure helper module — no Phaser scene-creation side effects on import.
 *   - Never touches src/game/skills/*.
 *   - Never auto-unloads a key while another active scene still uses it
 *     (callers responsible for declaring `PROTECTED_KEYS`; the helper itself
 *     trusts the caller's per-scene registry — overlapping registrations
 *     between scenes are allowed and tracked so unload only fires when the
 *     last owner releases).
 */

/**
 * Minimal interface accepted by the helper — matches the relevant slice of a
 * `Phaser.Scene` so tests can pass plain stubs without spinning up the full
 * engine.
 */
export interface SceneLike {
  /**
   * Scene plugin (Phaser.Scenes.ScenePlugin) — we only need `.key`. We
   * accept anything that exposes a `.key: string`; `Phaser.Scene`'s
   * `ScenePlugin` satisfies this structurally.
   */
  readonly scene: { readonly key: string };
  /**
   * Phaser textures cache slice we need. Real `Phaser.Textures.TextureManager`
   * returns the manager itself from `remove(key)` for chaining, but we don't
   * care about the return value — the wide `any` here accepts any return so a
   * real `TextureManager` is structurally assignable without an index signature
   * (which collides with the named members).
   */
  readonly textures: {
    exists(key: string): boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    remove(key: string): any;
  };
}

/**
 * Keys that must NEVER be unloaded by this helper, even if a caller registers
 * them by mistake. Add to this when introducing a new truly-shared asset.
 *
 * Currently protected:
 *   - `__DEFAULT`, `__MISSING`, `__WHITE`: Phaser-builtin textures (removing
 *     them would crash the renderer).
 */
export const PROTECTED_KEYS: ReadonlySet<string> = new Set([
  '__DEFAULT',
  '__MISSING',
  '__WHITE',
]);

/**
 * Per-scene-key registry: scene-key → set of asset keys it owns.
 *
 * Keyed by scene KEY (not the Scene instance) on purpose: Phaser destroys and
 * re-creates a Scene's internal state across stop/start cycles but keeps the
 * same key. Using the key makes register/unload pairs work cleanly across
 * those cycles. A reference-counted global table tracks how many scenes
 * currently "own" each asset key — a key is only actually removed from the
 * texture cache when the last owner releases it.
 */
const sceneRegistry = new Map<string, Set<string>>();
const globalRefCount = new Map<string, number>();

/**
 * Register asset keys as owned by `scene`. Idempotent — re-registering the
 * same key is a no-op (does not bump the refcount twice). Returns the set of
 * keys actually newly-registered (useful for tests).
 */
export function registerSceneAssets(scene: SceneLike, keys: readonly string[]): string[] {
  const sceneKey = scene.scene.key;
  let owned = sceneRegistry.get(sceneKey);
  if (!owned) {
    owned = new Set<string>();
    sceneRegistry.set(sceneKey, owned);
  }
  const newlyAdded: string[] = [];
  for (const k of keys) {
    if (owned.has(k)) continue;
    owned.add(k);
    globalRefCount.set(k, (globalRefCount.get(k) ?? 0) + 1);
    newlyAdded.push(k);
  }
  return newlyAdded;
}

/**
 * Release asset keys owned by `scene`. Calls `textures.remove(key)` only when
 * the global refcount drops to zero AND the key is not in `PROTECTED_KEYS`
 * AND the texture currently exists. Returns the keys that were actually
 * unloaded (useful for tests / 量測 reports).
 *
 * Idempotent: calling unload on a key not currently owned by `scene` is a
 * no-op. Calling unload twice in a row only unloads on the first call.
 */
export function unloadSceneAssets(scene: SceneLike, keys: readonly string[]): string[] {
  const sceneKey = scene.scene.key;
  const owned = sceneRegistry.get(sceneKey);
  if (!owned) return [];
  const unloaded: string[] = [];
  for (const k of keys) {
    if (!owned.has(k)) continue;
    owned.delete(k);
    const remaining = (globalRefCount.get(k) ?? 1) - 1;
    if (remaining <= 0) {
      globalRefCount.delete(k);
      if (!PROTECTED_KEYS.has(k) && scene.textures.exists(k)) {
        try {
          scene.textures.remove(k);
          unloaded.push(k);
        } catch {
          /* defensive: removal failure shouldn't break gameplay */
        }
      }
    } else {
      globalRefCount.set(k, remaining);
    }
  }
  if (owned.size === 0) sceneRegistry.delete(sceneKey);
  return unloaded;
}

/**
 * Release ALL asset keys owned by `scene` — convenience for shutdown hooks
 * that don't want to re-list every key registered earlier in create().
 */
export function unloadAllSceneAssets(scene: SceneLike): string[] {
  const sceneKey = scene.scene.key;
  const owned = sceneRegistry.get(sceneKey);
  if (!owned || owned.size === 0) return [];
  // Snapshot before unload mutates the set.
  const allKeys = Array.from(owned);
  return unloadSceneAssets(scene, allKeys);
}

/**
 * Get a snapshot of the keys currently registered to `scene`. Returns a new
 * array (safe to mutate); empty when no registration.
 */
export function getRegisteredKeys(scene: SceneLike): string[] {
  const owned = sceneRegistry.get(scene.scene.key);
  return owned ? Array.from(owned) : [];
}

/**
 * Inspection helper: how many scenes currently own this asset key globally?
 * Returns 0 when the key has never been registered (or has been fully released).
 */
export function getGlobalRefCount(key: string): number {
  return globalRefCount.get(key) ?? 0;
}

/**
 * Reset all internal registry state — TEST-ONLY. Production code should never
 * need this (registry is keyed by scene key, naturally scoped). Test helper
 * lets each `it` start with a clean slate without leaking state between cases.
 */
export function __resetRegistry(): void {
  sceneRegistry.clear();
  globalRefCount.clear();
}

// ── Memory measurement (P05 量測 before/after) ────────────────────────────────

/**
 * Subset of the non-standard `performance.memory` API exposed by Chromium.
 */
interface ChromiumPerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

/**
 * Read `performance.memory.usedJSHeapSize` and return it in MB. Returns
 * `null` when the API is unavailable (Firefox / Safari / node-vitest).
 *
 * Useful for instrumenting "before mode-switch" / "after mode-switch" memory
 * deltas without making call sites worry about cross-browser support.
 */
export function measureMemoryMB(): number | null {
  if (typeof performance === 'undefined') return null;
  const perfWithMem = performance as unknown as { memory?: ChromiumPerformanceMemory };
  const mem = perfWithMem.memory;
  if (!mem || typeof mem.usedJSHeapSize !== 'number') return null;
  return mem.usedJSHeapSize / (1024 * 1024);
}

// ── Mode-switch event (學習端 ↔ 戰鬥端) ────────────────────────────────────

/** Payload of the `ef:mode-switch` CustomEvent. */
export interface ModeSwitchDetail {
  /** Mode we're leaving — `null` on first boot. */
  from: 'learn' | 'battle' | null;
  /** Mode we're entering. */
  to: 'learn' | 'battle';
}

/**
 * Classify the high-level mode for a given `setMode()` argument so cross-mode
 * unload handlers don't have to enumerate every UI screen. Currently:
 *   - 'battle' — h-battle / tower / tutorial / title / ending (anything that
 *     mounts a Phaser scene)
 *   - 'learn'  — every DOM-only academy / lesson / dashboard screen
 *
 * Exported so main.ts and tests use the SAME classification.
 */
const BATTLE_MODES = new Set([
  'h-battle',
  'tower',
  'tutorial',
  'title',
  'ending',
]);

export function classifyMode(mode: string): 'learn' | 'battle' {
  return BATTLE_MODES.has(mode) ? 'battle' : 'learn';
}

/**
 * Fire `ef:mode-switch` on `window`. No-op when `window` is undefined
 * (vitest / node). Returns the dispatched detail (or null when skipped) so
 * tests can assert without spying on DOM APIs.
 */
export function dispatchModeSwitch(
  from: 'learn' | 'battle' | null,
  to: 'learn' | 'battle',
): ModeSwitchDetail | null {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return null;
  }
  const detail: ModeSwitchDetail = { from, to };
  try {
    window.dispatchEvent(new CustomEvent('ef:mode-switch', { detail }));
  } catch {
    /* defensive: some test environments lack CustomEvent constructor */
    return null;
  }
  return detail;
}
